import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PanAppState, PlatformEnum, PlatformService } from '@panamax/app-state';
import { BrowserDimensions } from '@panamax/app-state/lib/models/platform.model';
import { HelpModalComponent } from '@payments/components/help-modal/help-modal.component';
import { HelpTerms } from '@payments/constants/help-for-payment-activity';
import {
  PaymentActivityPageViewModel,
  PaymentRowText,
  PaymentViewModel,
} from '@payments/models/payment-activity-page-view-model';
import { HeaderFilterButtonStatus } from '@payments/payments-shared/header-filters/header-filters.component';
import { PaymentsBasePage } from '@payments/payments-shared/payments-base/payments-base.page';
import { CancelPendingPaymentsService } from '@payments/services/cancel-pending-payments/cancel-pending-payments.service';
import { InvoiceIsrService } from '@payments/services/invoice-isr.service';
import { ModalType } from '@payments/services/payments-modal.service';
import { PaymentsService } from '@payments/services/payments.service';
import { QuickFilter } from '@shared/components/usf-filtering-banner/model/quick-filter.model';
import { PageTitle } from '@shared/constants/page-titles.enum';
import { PATHS } from '@shared/constants/paths';
import { ModalService } from '@shared/services/modal/modal.service';
import { SortDirection } from '@usf/ngrx-payment';
import { PaymentActivitySortFields, Status } from '@usf/payment-types';
import { Observable, Subscription } from 'rxjs';
import { filter, skip, skipWhile, take } from 'rxjs/operators';
import {
  PaymentActivityService,
  PaymentFilterIds,
} from './services/payment-activity.service';

@Component({
  selector: 'app-payment-activity',
  templateUrl: './payment-activity.page.html',
  styleUrls: ['./payment-activity.page.scss'],
})
export class PaymentActivityPage extends PaymentsBasePage {
  @ViewChild(CdkVirtualScrollViewport)
  virtualScrollViewport: CdkVirtualScrollViewport;

  readonly PlatformEnum = PlatformEnum;
  readonly PendingStatus = Status.PENDING;
  readonly HeaderFilterButtonStatus = HeaderFilterButtonStatus;
  readonly ModalType = ModalType;
  readonly PaymentFilterIds = PaymentFilterIds;
  readonly SortFields = PaymentActivitySortFields;
  readonly SortDirection = SortDirection;

  browserDimensions: BrowserDimensions;
  subscriptions$: Subscription[] = [];

  isInitialLoading = false;
  isMobile: boolean;
  isTouch$: Observable<boolean>;
  sortDirection: SortDirection;
  sortField: PaymentActivitySortFields;
  title: string;
  paymentRowText: PaymentRowText;
  vm$: Observable<PaymentActivityPageViewModel>;
  canCancel = false;

  quickFilters: QuickFilter[] = [
    {
      id: [Status.PENDING, Status.CANCELLING],
      title: 'Pending',
      count: 0,
      color: 'red',
      selected: false,
      translationKey: 'i18n.payments.quickFilters.pending',
    },
    {
      id: [Status.PROCESSING],
      title: 'Processing',
      count: 0,
      color: 'green',
      selected: false,
      translationKey: 'i18n.payments.quickFilters.processing',
    },
    {
      id: [Status.PAID],
      title: 'Paid',
      count: 0,
      color: 'green',
      selected: false,
      translationKey: 'i18n.payments.quickFilters.paid',
    },
  ];

  constructor(
    public router: Router,
    readonly route: ActivatedRoute,
    readonly paymentsService: PaymentsService,
    readonly panAppState: PanAppState,
    readonly platformService: PlatformService,
    public paymentActivityService: PaymentActivityService,
    private translateService: TranslateService,
    private cancelPaymentsService: CancelPendingPaymentsService,
    private invoiceIsrService: InvoiceIsrService,
    private modalController: ModalController,
    private modalService: ModalService,
  ) {
    super(router, route, paymentsService);
    this.resetFilters();
  }

  actionsAfterCancelling = (): void => {
    this.paymentActivityService.fetchPaymentActivity(1);
    this.paymentActivityService.reloadStatusFilterSummary();
  };

  ionViewWillEnter() {
    super.ionViewWillEnter();

    const queryFilters = this.getFiltersFromParams();

    this.vm$ = this.paymentActivityService.getPaymentActivityPageViewModel$();
    this.paymentActivityService.setInitialSortAndFilter(queryFilters);
    this.browserDimensions = this.platformService.getBrowserDimensions();
    this.isTouch$ = this.platformService.isTouchExperience$;
    this.isMobile = this.platformService.platformType === PlatformEnum.mobile;
    this.panAppState.setPageTitle(
      this.translateService.instant(PageTitle.paymentActivity),
    );
    this.setTranslatedText();

    this.subscriptions$.push(
      this.cancelPaymentsService.canCancelPayment$().subscribe(canCancel => {
        if (canCancel) {
          this.canCancel = true;
        }
      }),
    );
    this.handleCustomerFilterChange();
  }

  ionViewWillLeave() {
    this.subscriptions$.forEach(subscription => subscription?.unsubscribe());
  }

  async openMoreOptions({
    event,
    payment,
    canCancel,
  }: {
    event: Event;
    payment: PaymentViewModel;
    canCancel: boolean;
  }) {
    const buttons: any[] = [];

    buttons.push(
      {
        text: this.translateService.instant('i18n.common.view'),
        handler: () => {
          this.openViewDetailsModal({ payment });
          this.invoiceIsrService.dismissPopover();
        },
      },
      {
        text: this.translateService.instant('i18n.common.download'),
        handler: () => {
          this.paymentActivityService.downloadPaymentDetails(
            payment.paymentActivityID,
          );
          this.invoiceIsrService.dismissPopover();
        },
      },
    );
    if (!Capacitor.isNativePlatform()) {
      buttons.push({
        text: this.translateService.instant('i18n.common.print'),
        handler: () => {
          this.paymentActivityService.printPaymentDetails(
            payment.paymentActivityID,
          );
          this.invoiceIsrService.dismissPopover();
        },
      });
    }

    if (canCancel) {
      buttons.push(
        this.cancelPaymentsService.cancelOption(
          payment.paymentActivityID,
          this.actionsAfterCancelling,
          true,
        ),
      );
    }
    buttons.push({
      text: this.translateService.instant('i18n.common.cancel'),
      role: 'cancel',
      cssClass: 'cancel-button',
      handler: () => {},
    });

    this.paymentActivityService.presentPaymentOptions(buttons, event);
  }

  openShareDownload(event: Event) {
    const buttons: any[] = [
      {
        text: this.translateService.instant('i18n.common.download'),
        handler: () => {
          this.downloadAllPayments();
          this.invoiceIsrService.dismissPopover();
        },
      },
      {
        text: this.translateService.instant('i18n.common.share'),
        handler: () => {
          this.shareAllPayments();
          this.invoiceIsrService.dismissPopover();
        },
      },
      {
        text: this.translateService.instant('i18n.common.cancel'),
        role: 'cancel',
        cssClass: 'cancel-button',
        handler: () => {},
      },
    ];

    this.paymentActivityService.presentPaymentOptions(buttons, event);
  }

  async openHelpModal() {
    this.modalService.setModalOptions(
      this.platformService.isTouch.value,
      HelpModalComponent,
      {
        terms: HelpTerms,
        sectionName: 'i18n.payments.paymentActivity.title',
      },
      'help-modal',
    );

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );

    await modal.present();
    return await modal.onDidDismiss();
  }

  resetFilters() {
    this.sortField = this.paymentActivityService.initialSort.fieldName;
    this.sortDirection = this.paymentActivityService.initialSort.sortValue;
  }

  setTranslatedText() {
    this.translateService
      .get('i18n.payments.paymentActivity.title')
      .pipe(take(1))
      .subscribe(_ => {
        this.title = this.translateService.instant(
          'i18n.payments.paymentActivity.title',
        );
        this.paymentRowText = {
          customers: this.translateService.instant(
            'i18n.payments.paymentRow.customers',
          ),
          items: this.translateService.instant(
            'i18n.payments.paymentRow.items',
          ),
          paymentDate: this.translateService.instant(
            'i18n.payments.paymentRow.paymentDate',
          ),
          paymentMethod: this.translateService.instant(
            'i18n.payments.paymentRow.paymentMethod',
          ),
          referenceNumber: this.translateService.instant(
            'i18n.payments.paymentRow.referenceNumber',
          ),
          paymentAmount: this.translateService.instant(
            'i18n.payments.paymentRow.paymentAmount',
          ),
          status: this.translateService.instant(
            'i18n.payments.paymentRow.status',
          ),
        };
      });
  }

  goBackToPaymentCenter = () => {
    this.router.navigate([PATHS.PAYMENTS]);
  };

  downloadAllPayments() {
    this.paymentActivityService.fetchPaymentActivity(1, 0, false);
    this.vm$.pipe(skip(1), take(1)).subscribe(vm => {
      if (vm.warning?.isWarning) {
        this.paymentsService.showToastMessage(
          vm.warning.messageText.toString(),
        );
        this.paymentActivityService.fetchPaymentActivity(1);
        return;
      }
      this.paymentActivityService.downloadPaymentActivity(vm.payments);
    });
  }

  shareAllPayments() {
    this.paymentActivityService.fetchPaymentActivity(1, 0);
    this.vm$.pipe(skip(1), take(1)).subscribe(vm => {
      const paymentDates = this.getPaymentDates(vm);
      this.paymentActivityService.sharePaymentActivity(
        vm.payments,
        this.convertFilterDate(paymentDates.fromPaymentDate),
        this.convertFilterDate(paymentDates.toPaymentDate),
      );
    });
  }

  printAllPayments() {
    this.paymentActivityService.fetchPaymentActivity(1, 0);
    this.vm$.pipe(skip(1), take(1)).subscribe(vm => {
      const paymentDates = this.getPaymentDates(vm);
      this.paymentActivityService.printPaymentActivity(
        vm.payments,
        this.convertFilterDate(paymentDates.fromPaymentDate),
        this.convertFilterDate(paymentDates.toPaymentDate),
      );
    });
  }

  handleSort({
    sortField,
    sortDirection,
  }: {
    sortField: string;
    sortDirection: SortDirection;
  }) {
    this.sort(sortField as PaymentActivitySortFields, sortDirection);
  }

  sort(
    field: PaymentActivitySortFields,
    direction: SortDirection = SortDirection.DESC,
  ) {
    if (field === this.sortField) {
      this.sortDirection =
        this.sortDirection === SortDirection.ASC
          ? SortDirection.DESC
          : SortDirection.ASC;
    } else {
      this.sortDirection = direction;
    }
    this.sortField = field;

    let sortFields = [{ fieldName: field, sortValue: this.sortDirection }];

    this.paymentActivityService.setSort(sortFields);
  }

  trackByIdx(i) {
    return i;
  }

  scrolledIndexChange(
    _event,
    pageNum: number,
    hasNextPage: boolean,
    isLoading: boolean,
  ) {
    if (isLoading) return;
    const end = this.virtualScrollViewport.getRenderedRange().end;
    const total = this.virtualScrollViewport.getDataLength();
    const nextPage = pageNum + 1;

    if (end === total && hasNextPage) {
      this.paymentActivityService.fetchPaymentActivity(nextPage);
    }
  }

  openViewDetailsModal({ payment }: { payment: PaymentViewModel }) {
    this.cancelPaymentsService.fetchPaymentDetails(
      payment.paymentActivityID,
      false,
      this.actionsAfterCancelling,
      this.canCancel,
    );
  }

  private getPaymentDates(vm: PaymentActivityPageViewModel): {
    fromPaymentDate: number;
    toPaymentDate: number;
  } {
    const fromPaymentDate =
      (vm.filters.find(f => f.filterName === PaymentFilterIds.fromPaymentDate)
        ?.value as number) || null;
    const toPaymentDate =
      (vm.filters.find(f => f.filterName === PaymentFilterIds.toPaymentDate)
        ?.value as number) || null;
    return { fromPaymentDate, toPaymentDate };
  }

  private handleCustomerFilterChange() {
    let initValue = true;
    let lastCustomerNumber: number;

    this.subscriptions$.push(
      this.paymentActivityService
        .getSelectedPaymentCustomers()
        .pipe(
          skipWhile(({ filters }) => !filters),
          filter(
            ({ paymentCustomers }) => paymentCustomers.customers.length > 0,
          ),
        )
        .subscribe(({ filters, paymentCustomers }) => {
          let currentCustomerNumber = paymentCustomers.customers[0];
          if (!initValue && currentCustomerNumber !== lastCustomerNumber) {
            const newFilters = filters.filter(
              filter => filter.filterName !== PaymentFilterIds.customers,
            );
            this.paymentActivityService.setFilter([
              ...newFilters,
              {
                filterName: PaymentFilterIds.customers,
                value: paymentCustomers.customers,
              },
            ]);
          }
          lastCustomerNumber = currentCustomerNumber;
          initValue = false;
        }),
    );
  }
}
